/**
 * Service for admin integration configuration management.
 */

import axiosInstance from '../../../config/axios';

export interface IntegrationConfig {
  provider: string;
  name: string;
  configured: boolean;
  source: 'env' | 'database' | null;
  is_active: boolean;
  client_id: string;
  redirect_uri: string;
  has_client_secret: boolean;
  extra_config?: Record<string, unknown>;
}

export interface IntegrationListResponse {
  integrations: IntegrationConfig[];
}

export interface SaveIntegrationRequest {
  client_id: string;
  client_secret?: string;
  redirect_uri: string;
  is_active?: boolean;
  extra_config?: Record<string, unknown>;
}

export interface SaveIntegrationResponse {
  provider: string;
  configured: boolean;
  source: string;
  message: string;
}

export interface DeleteIntegrationResponse {
  provider: string;
  deleted: boolean;
  message: string;
}

export interface ToggleIntegrationResponse {
  provider: string;
  is_active: boolean;
  message: string;
}

class IntegrationAdminService {
  private baseUrl = '/api/admin/integrations';

  async listIntegrations(): Promise<IntegrationConfig[]> {
    const response = await axiosInstance.get<IntegrationListResponse>(`${this.baseUrl}/`);
    return response.data.integrations;
  }

  async getIntegration(provider: string): Promise<IntegrationConfig> {
    const response = await axiosInstance.get<IntegrationConfig>(`${this.baseUrl}/${provider}/`);
    return response.data;
  }

  async saveIntegration(provider: string, data: SaveIntegrationRequest): Promise<SaveIntegrationResponse> {
    const response = await axiosInstance.post<SaveIntegrationResponse>(
      `${this.baseUrl}/${provider}/save/`,
      data
    );
    return response.data;
  }

  async deleteIntegration(provider: string): Promise<DeleteIntegrationResponse> {
    const response = await axiosInstance.delete<DeleteIntegrationResponse>(
      `${this.baseUrl}/${provider}/delete/`
    );
    return response.data;
  }

  async toggleIntegration(provider: string, isActive: boolean): Promise<ToggleIntegrationResponse> {
    const response = await axiosInstance.post<ToggleIntegrationResponse>(
      `${this.baseUrl}/${provider}/toggle/`,
      { is_active: isActive }
    );
    return response.data;
  }
}

export default new IntegrationAdminService();
